<script setup lang="ts">
import { bemBuilder } from "@chatfood/core-utils";
import {
  useImage,
  AtomCard,
  AtomText,
  SatMenuSelector,
  type ISatMenuSelectorProps,
  type ISatMenuSelectorEmits,
} from "@chatfood/design-system";
import { computed } from "vue";
import { useNavigation } from "~/composables/use-navigation";
import { useLocale } from "~/composables/use-locale";
import { useOutletStore } from "~/stores/outlet";
import { useMenusStore } from "~/stores/menus";
import type { IMenuSelectorProps } from "~/components/menu-selector/props";

const css = bemBuilder("menu-selector");

const props = defineProps<IMenuSelectorProps>();

const outletStore = useOutletStore();
const menusStore = useMenusStore();

const { t } = useLocale();
const { optimize } = useImage();
const { rawPush, resolvePath } = useNavigation();

const menus = computed<ISatMenuSelectorProps["menus"]>(() => {
  return menusStore.menus.map((menu) => ({
    id: menu.id,
    title: menu.name,
    summary: menu.summary,
    icon: menu.icon ? optimize(menu.icon, { w: 600 }) : undefined,
    link: resolvePath("menu", {
      menuSlug: menu.slug,
      orderingMode: props.orderingMode,
      outletSlug: outletStore.slug,
    }),
  }));
});

const onClick = (menu: ISatMenuSelectorEmits["select"][0]) => {
  rawPush(menu.link);
};
</script>

<template>
  <AtomCard :class="css('')">
    <SatMenuSelector v-if="menus.length" :menus="menus" @select="onClick" />
    <AtomText v-else :class="css('empty-menus')">
      {{ t("components.menu_selector.no_menus_message") }}
    </AtomText>
  </AtomCard>
</template>

<style lang="scss" scoped>
.menu-selector {
  &__empty-menus {
    display: block;
    text-align: center;
    padding-block: var(--spacing-2xl);
  }
}
</style>
